// Mobile-first artcast card with touch gestures and smart previews
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Global flag to track if hint has been shown (resets on page refresh)
let globalHintShown = false;
import {
    motion,
    AnimatePresence,
    useMotionValue,
    useTransform,
    PanInfo,
} from 'framer-motion';
import classNames from 'classnames';
import type { Channel } from '../types';
import type { DetailedChannel, DetailedArtwork } from '../types/artwork';
import { formatImageUrl, formatVideoUrl } from '../utils/formatUrl';
import {
    formatGenres,
    formatGenresWithEmojis,
    getGenreName,
    getGenreEmoji,
} from '../constants/genres';

interface MobileArtcastCardProps {
    channel: Channel;
    isSelected: boolean;
    onSelect: () => void;
    onPlayOnScreen?: () => void;
    isPreviewingGlobally?: boolean;
    onPreviewChange?: (isPreviewing: boolean) => void;
    onExpansionChange?: (isExpanded: boolean, channelId: string) => void;
    isExpanded?: boolean;
    initialAnimation?: any;
    animateAnimation?: any;
    transitionAnimation?: any;
    isHidden?: boolean;
    hasDeviceParams?: boolean;
    isGift?: boolean;
}

export default function MobileArtcastCard({
    channel,
    isSelected,
    onSelect,
    onPlayOnScreen,
    isPreviewingGlobally = false,
    onPreviewChange,
    onExpansionChange,
    isExpanded: externalIsExpanded,
    initialAnimation,
    animateAnimation,
    transitionAnimation,
    isHidden = false,
    hasDeviceParams = false,
    isGift = false,
}: MobileArtcastCardProps) {
    // Initialize all hooks at the VERY TOP (before any conditional logic or returns)
    // This is REQUIRED by React's Rules of Hooks
    const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
    const [isInView, setIsInView] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [showPlayButton, setShowPlayButton] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [previewStartTime, setPreviewStartTime] = useState<number | null>(
        null
    );
    const [isSwipingArtwork, setIsSwipingArtwork] = useState(false);
    const [showDesktopNav, setShowDesktopNav] = useState(false);
    const [showSwipeHint, setShowSwipeHint] = useState(false);

    // Video loading state management
    const [videoReadyStates, setVideoReadyStates] = useState<
        Map<string, boolean>
    >(new Map());
    const preloadedImages = useRef<Set<string>>(new Set());

    // Use external expansion state if provided, otherwise use internal state
    const isExpanded = externalIsExpanded ?? false;
    const cardRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef(0);
    const touchStartTime = useRef(0);
    const touchHandled = useRef(false);
    const scrollEndTimer = useRef<NodeJS.Timeout | null>(null);
    const buttonVisibilityTimer = useRef<NodeJS.Timeout | null>(null);
    const desktopNavTimer = useRef<NodeJS.Timeout | null>(null);

    // Debug logging for received channel
    console.log(`🎭 MobileArtcastCard - Channel "${channel?.name}" received:`, {
        id: channel?.id,
        name: channel?.name,
        isGift: isGift,
        channelIsGift: channel?.isGift,
        artworksCount: channel?.artworks?.length || 0,
        artworks: channel?.artworks,
        hasCollections: !!(channel as any)?.collections?.length,
        collections: (channel as any)?.collections,
    });

    // Early return with error boundary if critical props are missing
    if (!channel || !channel.artworks) {
        console.error('[MobileArtcastCard] Missing critical props:', {
            channel,
            artworks: channel?.artworks,
        });
        return (
            <div className="relative rounded-2xl bg-gradient-to-br from-gray-800 to-gray-850 border-2 border-gray-700 p-4">
                <div className="text-white text-center">
                    Error loading artcast
                </div>
            </div>
        );
    }

    try {
        // Cast to DetailedChannel for better type support
        const detailedChannel = channel as unknown as DetailedChannel & {
            creator?: {
                display_name: string;
                user_id?: string;
            };
        };

        // Removed selectedChannel state - not needed anymore

        // Calculate distance from viewport center for scroll-based preview
        const calculateDistanceFromCenter = useCallback(() => {
            if (!cardRef.current || typeof window === 'undefined')
                return Infinity;

            const rect = cardRef.current.getBoundingClientRect();
            const viewportCenter = window.innerHeight / 2;
            const cardCenter = rect.top + rect.height / 2;

            // Return distance from viewport center
            return Math.abs(cardCenter - viewportCenter);
        }, []);

        // Intersection Observer for visibility tracking
        useEffect(() => {
            if (typeof window === 'undefined') return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    const inView =
                        entry.isIntersecting && entry.intersectionRatio > 0.3;
                    setIsInView(inView);
                    if (!inView) {
                        setIsPreviewing(false);
                        onPreviewChange?.(false);
                    }
                },
                { threshold: [0.3, 0.5, 0.8] }
            );

            if (cardRef.current) {
                observer.observe(cardRef.current);
            }

            return () => observer.disconnect();
        }, [onPreviewChange]);

        // Scroll-based preview selection
        useEffect(() => {
            if (!isInView || typeof window === 'undefined') return;

            const handleScroll = () => {
                // Use requestAnimationFrame for smooth updates synchronized with browser paint
                requestAnimationFrame(() => {
                    const distance = calculateDistanceFromCenter();

                    // Check if we're at the bottom of the page
                    const scrollHeight = document.documentElement.scrollHeight;
                    const scrollTop = window.scrollY;
                    const clientHeight = window.innerHeight;
                    const isAtBottom =
                        scrollTop + clientHeight >= scrollHeight - 10;

                    // Check if this card is visible at bottom of viewport
                    const rect = cardRef.current?.getBoundingClientRect();
                    const isLastCardVisible =
                        rect &&
                        rect.top < clientHeight &&
                        rect.bottom > clientHeight - 200;

                    // If this card is closest to center OR it's the last visible card at bottom
                    if (
                        (distance < 200 || (isAtBottom && isLastCardVisible)) &&
                        !isPreviewing &&
                        !isPreviewingGlobally
                    ) {
                        console.log(
                            '🎆 Starting scroll-based preview for channel:',
                            channel.name
                        );
                        setIsPreviewing(true);
                        onPreviewChange?.(true);
                    }
                    // If this card is far from center and currently previewing
                    else if (distance > 300 && isPreviewing && !isAtBottom) {
                        console.log(
                            '🛑 Stopping scroll-based preview for channel:',
                            channel.name
                        );
                        setIsPreviewing(false);
                        onPreviewChange?.(false);
                    }
                });
            };

            // Initial check
            handleScroll();

            // Listen to scroll events
            window.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }, [
            isInView,
            isPreviewing,
            isPreviewingGlobally,
            calculateDistanceFromCenter,
            channel.name,
            onPreviewChange,
        ]);

        // Removed auto-close behavior - view all should only close on user interaction
        // The expanded state is now controlled only by user clicks on the View All button

        // Scroll end detection with debouncing
        useEffect(() => {
            if (typeof window === 'undefined') return;

            const handleScroll = () => {
                // Set scrolling state to true
                setIsScrolling(true);

                // Clear existing timer
                if (scrollEndTimer.current) {
                    clearTimeout(scrollEndTimer.current);
                }

                // Set new timer for scroll end detection (300ms after scroll stops)
                scrollEndTimer.current = setTimeout(() => {
                    setIsScrolling(false);
                }, 300);
            };

            window.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                window.removeEventListener('scroll', handleScroll);
                if (scrollEndTimer.current) {
                    clearTimeout(scrollEndTimer.current);
                }
            };
        }, []);

        // Track when preview starts/stops
        useEffect(() => {
            if (isPreviewing) {
                setPreviewStartTime(Date.now());
            } else {
                setPreviewStartTime(null);
                setShowPlayButton(false);
                if (buttonVisibilityTimer.current) {
                    clearTimeout(buttonVisibilityTimer.current);
                }
            }
        }, [isPreviewing]);

        // Manage button visibility based on scroll state and preview timing
        useEffect(() => {
            // Clear any existing timer
            if (buttonVisibilityTimer.current) {
                clearTimeout(buttonVisibilityTimer.current);
            }

            // If button is already shown and still previewing, keep it visible
            if (showPlayButton && isPreviewing) {
                return;
            }

            // Check if conditions are met for initially showing the button
            if (
                !isScrolling &&
                isPreviewing &&
                previewStartTime &&
                !showPlayButton
            ) {
                const timeSincePreviewStart = Date.now() - previewStartTime;

                // If preview has been running for at least 1 second, show button
                if (timeSincePreviewStart >= 1000) {
                    setShowPlayButton(true);
                } else {
                    // Set timer to show button after remaining time
                    const remainingTime = 1000 - timeSincePreviewStart;
                    buttonVisibilityTimer.current = setTimeout(() => {
                        // Re-check conditions before showing
                        if (!isScrolling && isPreviewing) {
                            setShowPlayButton(true);
                        }
                    }, remainingTime);
                }
            }

            return () => {
                if (buttonVisibilityTimer.current) {
                    clearTimeout(buttonVisibilityTimer.current);
                }
            };
        }, [isScrolling, isPreviewing, previewStartTime, showPlayButton]);

        // States for dual preview phases
        const [previewPhase, setPreviewPhase] = useState<
            'beginning' | 'middle'
        >('beginning');

        // Get current artwork - defined early to avoid initialization issues
        const currentArtwork = channel.artworks?.[currentArtworkIndex];
        const detailedArtwork = currentArtwork as unknown as DetailedArtwork;

        // Don't add any preview coordination logic here - it's handled by the timer

        // Show swipe hint when preview starts on mobile with multiple artworks
        useEffect(() => {
            if (
                isPreviewing &&
                channel.artworks?.length > 1 &&
                !globalHintShown &&
                typeof window !== 'undefined' &&
                window.innerWidth < 768
            ) {
                // Mark that we've shown the hint globally
                globalHintShown = true;

                // Show hint after a short delay to let the preview settle
                const showTimer = setTimeout(() => {
                    setShowSwipeHint(true);
                }, 500);

                // Auto-hide after 3 seconds
                const hideTimer = setTimeout(() => {
                    setShowSwipeHint(false);
                }, 3500);

                return () => {
                    clearTimeout(showTimer);
                    clearTimeout(hideTimer);
                };
            }

            // IMPORTANT: Always hide the hint when preview stops
            // This prevents it from reappearing on subsequent previews
            if (!isPreviewing) {
                setShowSwipeHint(false);
            }
        }, [isPreviewing, channel.artworks?.length]);

        // Auto-rotate through artworks when previewing (images only show from start)
        useEffect(() => {
            if (!isPreviewing || !channel.artworks) return;

            // For single artwork, no rotation needed but still set up phase switching for videos
            if (channel.artworks.length <= 1) {
                if (currentArtwork && isVideo(currentArtwork)) {
                    const switchPhase = () => {
                        setPreviewPhase((prev) => {
                            const newPhase =
                                prev === 'beginning' ? 'middle' : 'beginning';
                            return newPhase;
                        });
                    };

                    const phaseTimer = setInterval(switchPhase, 6000); // Switch phase every 6 seconds
                    return () => clearInterval(phaseTimer);
                }
                return; // No timers needed for single image
            }

            // Starting auto-rotation for channel.artworks.length artworks

            // For images: Only show from beginning (no dual phases)
            // For videos: Still use dual phases (beginning + middle)
            let phaseTimer: NodeJS.Timeout;
            let artworkTimer: NodeJS.Timeout;

            const rotateArtwork = () => {
                setIsSwipingArtwork(true);
                setCurrentArtworkIndex((prev) => {
                    const next = (prev + 1) % channel.artworks.length;
                    // Rotating to artwork next + 1 of channel.artworks.length
                    setPreviewPhase('beginning'); // Reset to beginning phase for new artwork
                    return next;
                });
                // Reset swipe state after animation
                setTimeout(() => setIsSwipingArtwork(false), 300);
            };

            const switchPhase = () => {
                // Only switch phases for videos, not images
                if (currentArtwork && isVideo(currentArtwork)) {
                    setPreviewPhase((prev) => {
                        const newPhase =
                            prev === 'beginning' ? 'middle' : 'beginning';
                        // Switching to newPhase phase for video
                        return newPhase;
                    });
                }
            };

            // For videos: dual-phase cycle (12 seconds per artwork)
            // For images: single phase cycle (6 seconds per artwork)
            if (currentArtwork && isVideo(currentArtwork)) {
                phaseTimer = setInterval(switchPhase, 6000); // Switch phase every 6 seconds
                artworkTimer = setInterval(rotateArtwork, 12000); // Switch artwork every 12 seconds
            } else {
                // Images only: rotate every 6 seconds, no phase switching
                artworkTimer = setInterval(rotateArtwork, 6000); // Switch artwork every 6 seconds
            }

            return () => {
                clearInterval(phaseTimer);
                clearInterval(artworkTimer);
            };
        }, [isPreviewing, channel.artworks, currentArtwork]);

        // Helper function to get artwork thumbnail URL
        const getArtworkThumbnail = useCallback((artwork: any) => {
            const detailedArt = artwork as unknown as DetailedArtwork;

            if (detailedArt.thumbnail_path) {
                return formatImageUrl(detailedArt.thumbnail_path);
            }
            // Note: thumbnail property fallback removed as it's not in DetailedArtwork type

            return formatImageUrl(null);
        }, []);

        // Preload all artwork thumbnails when preview starts
        useEffect(() => {
            if (isPreviewing && channel.artworks?.length > 0) {
                // Preload all artwork thumbnails to prevent black screens
                channel.artworks.forEach((artwork) => {
                    const thumbnailUrl = formatImageUrl(
                        getArtworkThumbnail(artwork)
                    );
                    if (
                        thumbnailUrl &&
                        !preloadedImages.current.has(thumbnailUrl)
                    ) {
                        const img = new Image();
                        img.src = thumbnailUrl;
                        preloadedImages.current.add(thumbnailUrl);
                        console.log(
                            '📸 Preloading artwork thumbnail:',
                            thumbnailUrl
                        );
                    }
                });
            }
        }, [isPreviewing, channel.artworks, getArtworkThumbnail]);

        // Helper function to check if artwork is a video (includes GIFs which are treated as MP4 videos)
        const isVideo = (artwork: any) => {
            const detailedArt = artwork as unknown as DetailedArtwork;
            // Follow the AngularJS pattern: check file_metadata.media_type for 'VIDEO'
            const mediaType = detailedArt.file_metadata?.media_type;

            // Check for video media types and GIFs (which are treated as videos in NIIO)
            return (
                mediaType === 'VIDEO' ||
                mediaType?.toUpperCase() === 'VIDEO' ||
                mediaType?.toLowerCase().includes('video') ||
                mediaType === 'GIF' ||
                mediaType?.toUpperCase() === 'GIF' ||
                mediaType?.toLowerCase().includes('gif')
            );
        };

        // Memoize the current video URL to prevent continuous recalculation
        const currentVideoUrl = useMemo(() => {
            if (isPreviewing && currentArtwork && isVideo(currentArtwork)) {
                const artwork = currentArtwork as unknown as DetailedArtwork;

                // Debug video URL generation
                if (process.env.NODE_ENV === 'development') {
                    console.log(
                        '🎬 Video URL check:',
                        'isPreviewing:',
                        isPreviewing,
                        'hasArtwork:',
                        !!currentArtwork,
                        'isVideo:',
                        isVideo(currentArtwork),
                        'preview_art_tour:',
                        artwork.preview_art_tour
                    );
                }

                // Use preview_art_tour which contains MP4 URLs
                if (artwork.preview_art_tour) {
                    return formatVideoUrl(artwork.preview_art_tour);
                }

                // Fall back to regular preview if no MP4 available
                if (artwork.preview) {
                    return formatVideoUrl(artwork.preview);
                }

                // No video preview URL found
            }
            return null;
        }, [isPreviewing, currentArtworkIndex, previewPhase, currentArtwork]);

        // Handle video seeking when phase changes
        useEffect(() => {
            if (currentVideoUrl && isPreviewing) {
                const videoElement = document.querySelector(
                    'video[data-testid="video-preview"]'
                ) as HTMLVideoElement;
                if (videoElement && videoElement.readyState >= 2) {
                    // HAVE_CURRENT_DATA or higher
                    if (
                        previewPhase === 'middle' &&
                        videoElement.duration > 0
                    ) {
                        videoElement.currentTime = videoElement.duration * 0.5;
                        console.log(
                            '📍 Seeking video to middle:',
                            videoElement.duration * 0.5
                        );
                    } else {
                        videoElement.currentTime = 0;
                        console.log('📍 Seeking video to beginning');
                    }
                }
            }
        }, [previewPhase, currentVideoUrl, isPreviewing]);

        // Memoize the cover image URL to prevent continuous recalculation
        const coverImageUrl = useMemo(() => {
            // When previewing, show current artwork thumbnail using thumbnail_path
            if (isPreviewing && currentArtwork) {
                const detailedArt =
                    currentArtwork as unknown as DetailedArtwork;
                if (detailedArt.thumbnail_path) {
                    return formatImageUrl(detailedArt.thumbnail_path);
                }
                // Note: thumbnail property fallback removed as it's not in DetailedArtwork type
            }

            // Priority order for static display:
            // 1. Channel cover path (highest priority) - uses cover.path
            // 2. Channel thumbnail
            // 3. First artwork thumbnail_path
            // 4. First artwork thumbnail
            // 5. Placeholder

            if (detailedChannel.cover?.path) {
                return formatImageUrl(detailedChannel.cover.path);
            }

            if (detailedChannel.thumbnail) {
                return formatImageUrl(detailedChannel.thumbnail);
            }

            if (channel.artworks?.[0]) {
                const firstArtwork = channel
                    .artworks[0] as unknown as DetailedArtwork;
                if (firstArtwork.thumbnail_path) {
                    return formatImageUrl(firstArtwork.thumbnail_path);
                }
                // Note: thumbnail property fallback removed as it's not in DetailedArtwork type
            }

            return formatImageUrl(null); // Will return the data URL placeholder
        }, [
            isPreviewing,
            currentArtworkIndex,
            detailedChannel.cover?.path,
            detailedChannel.thumbnail,
            currentArtwork,
        ]);

        // Touch handlers for swipe navigation
        const handleTouchStart = (e: React.TouchEvent) => {
            touchStartX.current = e.touches[0].clientX;
            touchStartTime.current = Date.now();
            touchHandled.current = false;
            // Don't stop preview on touch start - wait to see if it's a swipe
        };

        const handleTouchEnd = (e: React.TouchEvent) => {
            const touchEndX = e.changedTouches[0].clientX;
            const touchDuration = Date.now() - touchStartTime.current;
            const swipeDistance = touchEndX - touchStartX.current;

            // Only handle swipe navigation - NO selection on tap
            // Swipe to navigate artworks (only if not a scroll gesture)
            if (Math.abs(swipeDistance) > 50 && touchDuration < 500) {
                touchHandled.current = true;

                // Hide swipe hint
                setShowSwipeHint(false);

                // Don't stop preview when swiping - maintain continuous preview experience
                // This prevents the PLAY ON SCREEN button from flickering

                if (swipeDistance > 0 && currentArtworkIndex > 0) {
                    setIsSwipingArtwork(true);
                    setCurrentArtworkIndex((prev) => prev - 1);
                    // Reset swipe state after animation
                    setTimeout(() => setIsSwipingArtwork(false), 300);
                } else if (
                    swipeDistance < 0 &&
                    currentArtworkIndex < channel.artworks.length - 1
                ) {
                    setIsSwipingArtwork(true);
                    setCurrentArtworkIndex((prev) => prev + 1);
                    // Reset swipe state after animation
                    setTimeout(() => setIsSwipingArtwork(false), 300);
                }
            }
        };

        // Desktop navigation handlers with circular navigation
        const handleDesktopPrevious = () => {
            if (currentArtworkIndex > 0) {
                setCurrentArtworkIndex((prev) => prev - 1);
            } else {
                // Wrap around to the last artwork
                setCurrentArtworkIndex(channel.artworks.length - 1);
            }
        };

        const handleDesktopNext = () => {
            if (currentArtworkIndex < channel.artworks.length - 1) {
                setCurrentArtworkIndex((prev) => prev + 1);
            } else {
                // Wrap around to the first artwork
                setCurrentArtworkIndex(0);
            }
        };

        // Show desktop navigation arrows temporarily
        const showDesktopNavigation = () => {
            if (typeof window !== 'undefined' && window.innerWidth >= 768) {
                setShowDesktopNav(true);

                // Clear existing timer
                if (desktopNavTimer.current) {
                    clearTimeout(desktopNavTimer.current);
                }

                // Hide after 2 seconds
                desktopNavTimer.current = setTimeout(() => {
                    setShowDesktopNav(false);
                }, 2000);
            }
        };

        // Hide desktop navigation arrows
        const hideDesktopNavigation = () => {
            setShowDesktopNav(false);
            if (desktopNavTimer.current) {
                clearTimeout(desktopNavTimer.current);
            }
        };

        // Keyboard navigation for desktop
        useEffect(() => {
            if (
                !isPreviewing ||
                typeof window === 'undefined' ||
                window.innerWidth < 768
            )
                return;

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handleDesktopPrevious();
                    showDesktopNavigation();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleDesktopNext();
                    showDesktopNavigation();
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                if (desktopNavTimer.current) {
                    clearTimeout(desktopNavTimer.current);
                }
            };
        }, [isPreviewing, currentArtworkIndex, channel.artworks.length]);

        // Show navigation arrows briefly when preview starts to hint at the feature
        // Then hide them unless hovering
        useEffect(() => {
            if (
                isPreviewing &&
                typeof window !== 'undefined' &&
                window.innerWidth >= 768 &&
                channel.artworks?.length > 1
            ) {
                // Show arrows briefly when preview starts
                setShowDesktopNav(true);

                // Clear any existing timer
                if (desktopNavTimer.current) {
                    clearTimeout(desktopNavTimer.current);
                }

                // Hide after 2 seconds to let user know about the feature
                desktopNavTimer.current = setTimeout(() => {
                    setShowDesktopNav(false);
                }, 2000);
            } else if (!isPreviewing) {
                hideDesktopNavigation();
            }

            return () => {
                if (desktopNavTimer.current) {
                    clearTimeout(desktopNavTimer.current);
                }
            };
        }, [isPreviewing]);

        return (
            <motion.div
                className="relative p-2 artcast-snap-item"
                style={{
                    scrollSnapAlign: 'start',
                    scrollMarginTop: '80px',
                    overflow: 'hidden',
                    pointerEvents: isHidden ? 'none' : 'auto',
                    display: isHidden ? 'none' : 'block',
                }}
                initial={initialAnimation}
                animate={animateAnimation}
                transition={transitionAnimation}
            >
                <motion.div
                    ref={cardRef}
                    className={classNames(
                        'relative rounded-2xl bg-gradient-to-br from-gray-800 to-gray-850',
                        'border-2 transition-colors duration-75 w-full cursor-pointer flex flex-col',
                        'artcast-card-container',
                        isGift || channel.isGift
                            ? isSelected
                                ? 'border-green-500 shadow-xl shadow-green-500/20 ring-2 ring-green-500/10'
                                : isPreviewing
                                ? 'border-green-500'
                                : 'border-gray-700 active:border-green-400 md:hover:border-green-400 md:hover:shadow-lg md:hover:shadow-green-500/10'
                            : isSelected
                            ? 'border-purple-500 shadow-xl shadow-purple-500/20 ring-2 ring-purple-500/10'
                            : isPreviewing
                            ? 'border-purple-500'
                            : 'border-gray-700 active:border-purple-400 md:hover:border-purple-400 md:hover:shadow-lg md:hover:shadow-purple-500/10'
                    )}
                    style={{ maxWidth: '100%', boxSizing: 'border-box' }}
                    data-testid="artcast-card"
                    role="button"
                    aria-label={`Select ${channel.name} artcast with ${
                        channel.artworks?.length || 0
                    } artworks`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: isSelected ? 1.01 : 1,
                        borderColor: isGift
                            ? isPreviewing
                                ? '#10b981' // green-500
                                : isSelected
                                ? '#10b981' // green-500
                                : '#374151'
                            : isPreviewing
                            ? '#a855f7'
                            : isSelected
                            ? '#a855f7'
                            : '#374151',
                        boxShadow: isGift
                            ? isPreviewing
                                ? '0 0 0 2px rgba(16, 185, 129, 0.5), 0 8px 32px rgba(16, 185, 129, 0.3)'
                                : isSelected
                                ? '0 25px 50px -12px rgba(16, 185, 129, 0.25), 0 0 0 1px rgba(16, 185, 129, 0.1)'
                                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            : isPreviewing
                            ? '0 0 0 2px rgba(168, 85, 247, 0.5), 0 8px 32px rgba(168, 85, 247, 0.3)'
                            : isSelected
                            ? '0 25px 50px -12px rgba(168, 85, 247, 0.25), 0 0 0 1px rgba(168, 85, 247, 0.1)'
                            : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                    whileTap={{ scale: isSelected ? 0.99 : 0.98 }}
                    whileHover={{
                        scale: isSelected ? 1.02 : 1.01,
                        borderColor:
                            isGift || channel.isGift ? '#10b981' : '#a855f7',
                        boxShadow:
                            isGift || channel.isGift
                                ? '0 0 0 2px rgba(16, 185, 129, 0.5), 0 8px 32px rgba(16, 185, 129, 0.3)'
                                : '0 0 0 2px rgba(168, 85, 247, 0.5), 0 8px 32px rgba(168, 85, 247, 0.3)',
                        transition: { duration: 0.2 },
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Enhanced border glow effect when selected */}
                    {isSelected && (
                        <motion.div
                            className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{
                                type: 'spring',
                                stiffness: 300,
                                damping: 30,
                            }}
                        >
                            <div className="absolute inset-[-4px] rounded-2xl bg-purple-500/20 blur-xl animate-pulse" />
                            <div className="absolute inset-[-2px] rounded-2xl bg-purple-400/30 blur-lg" />
                        </motion.div>
                    )}

                    {/* Main Cover Section - Ensure proper stacking context */}
                    <div
                        className="relative h-52 sm:h-60 rounded-2xl w-full cover-section overflow-hidden"
                        data-testid="card-cover"
                    >
                        {/* Always render thumbnail as base layer */}
                        <motion.img
                            key={`image-base-${channel.id}-${
                                isPreviewing ? currentArtworkIndex : 'static'
                            }`}
                            src={coverImageUrl}
                            alt={channel.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(
                                e: React.SyntheticEvent<HTMLImageElement>
                            ) => {
                                // Image failed to load, but the placeholder is already a data URL
                                // so it won't fail. This is just for safety.
                                e.currentTarget.style.opacity = '0.5';
                            }}
                            onLoad={() => {
                                // Image loaded successfully
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        />

                        {/* Video element - always rendered but visibility controlled */}
                        {currentVideoUrl && (
                            <motion.video
                                key={`video-${currentArtworkIndex}`}
                                src={currentVideoUrl}
                                className="absolute inset-0 w-full h-full object-cover z-10"
                                data-testid="video-preview"
                                aria-label={`Video preview of ${
                                    currentArtwork?.title || 'artwork'
                                }`}
                                autoPlay
                                muted
                                playsInline
                                loop
                                controls={false}
                                preload="metadata"
                                style={{
                                    opacity: videoReadyStates.get(
                                        currentVideoUrl
                                    )
                                        ? 1
                                        : 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                }}
                                onError={() => {
                                    // Video failed to load, remove from ready states
                                    setVideoReadyStates((prev) => {
                                        const next = new Map(prev);
                                        next.delete(currentVideoUrl);
                                        return next;
                                    });
                                }}
                                onLoadStart={() => {
                                    // Video loading started - mark as not ready
                                    console.log(
                                        '🎬 Video loading started:',
                                        currentVideoUrl
                                    );
                                    setVideoReadyStates((prev) => {
                                        const next = new Map(prev);
                                        next.set(currentVideoUrl, false);
                                        return next;
                                    });
                                }}
                                onCanPlay={() => {
                                    // Video ready to play - mark as ready
                                    console.log(
                                        '✅ Video ready to play:',
                                        currentVideoUrl
                                    );
                                    setVideoReadyStates((prev) => {
                                        const next = new Map(prev);
                                        next.set(currentVideoUrl, true);
                                        return next;
                                    });
                                }}
                                onLoadedData={(
                                    e: React.SyntheticEvent<HTMLVideoElement>
                                ) => {
                                    // Set the video start time based on preview phase
                                    const video = e.currentTarget;
                                    if (
                                        previewPhase === 'middle' &&
                                        video.duration > 0
                                    ) {
                                        video.currentTime =
                                            video.duration * 0.5; // Start from middle
                                    } else {
                                        video.currentTime = 0; // Start from beginning
                                    }
                                }}
                            />
                        )}

                        {/* Desktop Navigation Arrows */}
                        {isPreviewing && channel.artworks?.length > 1 && (
                            <div
                                className="hidden md:block"
                                onMouseEnter={showDesktopNavigation}
                                onMouseLeave={hideDesktopNavigation}
                            >
                                {/* Left navigation area */}
                                <motion.button
                                    className="absolute left-0 top-0 h-full w-1/3 flex items-center justify-start pl-4 bg-gradient-to-r from-black/30 to-transparent cursor-pointer z-10"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        handleDesktopPrevious();
                                        showDesktopNavigation();
                                    }}
                                    aria-label="Previous artwork"
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: showDesktopNav ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="bg-white/10 backdrop-blur-sm rounded-full p-2 hover:bg-white/20 transition-colors">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </div>
                                </motion.button>

                                {/* Right navigation area */}
                                <motion.button
                                    className="absolute right-0 top-0 h-full w-1/3 flex items-center justify-end pr-4 bg-gradient-to-l from-black/30 to-transparent cursor-pointer z-10"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        handleDesktopNext();
                                        showDesktopNavigation();
                                    }}
                                    aria-label="Next artwork"
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: showDesktopNav ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="bg-white/10 backdrop-blur-sm rounded-full p-2 hover:bg-white/20 transition-colors">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </motion.button>
                            </div>
                        )}

                        {/* Removed dark overlay as per requirements */}

                        {/* Swipe Hint Overlay - Mobile Only */}
                        <AnimatePresence>
                            {showSwipeHint && isPreviewing && (
                                <motion.div
                                    className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center md:hidden"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Semi-transparent background for contrast */}
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30" />

                                    {/* Swipe gesture indicators */}
                                    <div className="relative flex items-center gap-6">
                                        {/* Left swipe indicator */}
                                        <motion.div
                                            className="flex items-center"
                                            animate={{
                                                x: [-15, 0, -15],
                                                opacity: [0.6, 1, 0.6],
                                            }}
                                            transition={{
                                                duration: 1.5,
                                                repeat: Infinity,
                                                ease: 'easeInOut',
                                            }}
                                        >
                                            <svg
                                                className="w-10 h-10 text-white drop-shadow-lg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    d="M15 19l-7-7 7-7"
                                                />
                                            </svg>
                                        </motion.div>

                                        {/* Center text */}
                                        <motion.div
                                            className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full"
                                            initial={{ scale: 0.9, y: 10 }}
                                            animate={{ scale: 1, y: 0 }}
                                            transition={{
                                                duration: 0.4,
                                                delay: 0.2,
                                            }}
                                        >
                                            <p className="text-white text-sm font-medium whitespace-nowrap">
                                                Swipe to explore
                                            </p>
                                        </motion.div>

                                        {/* Right swipe indicator */}
                                        <motion.div
                                            className="flex items-center"
                                            animate={{
                                                x: [15, 0, 15],
                                                opacity: [0.6, 1, 0.6],
                                            }}
                                            transition={{
                                                duration: 1.5,
                                                repeat: Infinity,
                                                ease: 'easeInOut',
                                                delay: 0.75,
                                            }}
                                        >
                                            <svg
                                                className="w-10 h-10 text-white drop-shadow-lg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </motion.div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Removed Preview Indicator and Artwork Count as per requirements */}

                        {/* Removed Selection Circle as per requirements - button will be moved inside card content */}
                    </div>

                    {/* Channel Info */}
                    <div className="p-4 sm:p-5 w-full min-w-0 mobile-card-content overflow-hidden">
                        {/* Artwork Navigation Dots - Moved to top of content area */}
                        {channel.artworks?.length > 1 && (
                            <div className="mb-3">
                                <div
                                    className="flex gap-1 items-center justify-center"
                                    data-testid="artwork-dots"
                                >
                                    {channel.artworks.map((_, index) => (
                                        <motion.div
                                            key={index}
                                            className={classNames(
                                                'h-1.5 rounded-full transition-all duration-300 relative overflow-hidden flex-shrink-0',
                                                index === currentArtworkIndex
                                                    ? 'bg-white w-8'
                                                    : 'bg-gray-600 w-1.5'
                                            )}
                                            animate={{
                                                scale:
                                                    index ===
                                                    currentArtworkIndex
                                                        ? [1, 1.1, 1]
                                                        : 1,
                                            }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            {/* Progress indicator for current artwork during preview */}
                                            {isPreviewing &&
                                                index ===
                                                    currentArtworkIndex && (
                                                    <motion.div
                                                        key={`progress-${index}-${currentArtworkIndex}`}
                                                        className="absolute inset-0 rounded-full overflow-hidden"
                                                        style={{
                                                            height: '100%',
                                                            borderRadius:
                                                                '9999px',
                                                            backgroundColor:
                                                                'rgba(75, 85, 99, 0.6)', // Darker gray base
                                                        }}
                                                    >
                                                        <motion.div
                                                            className="h-full rounded-full"
                                                            initial={{
                                                                width: 0,
                                                            }}
                                                            animate={{
                                                                width: '100%',
                                                            }}
                                                            transition={{
                                                                duration:
                                                                    currentArtwork &&
                                                                    isVideo(
                                                                        currentArtwork
                                                                    )
                                                                        ? 12
                                                                        : 6, // 12s for videos, 6s for images
                                                                ease: 'linear',
                                                                repeat: Infinity,
                                                            }}
                                                            style={{
                                                                borderRadius:
                                                                    '9999px',
                                                                background:
                                                                    '#ffffff', // Solid white color for clear progress
                                                                boxShadow:
                                                                    '0 0 4px rgba(255, 255, 255, 0.6)',
                                                            }}
                                                        />
                                                    </motion.div>
                                                )}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="relative">
                            <h3
                                className="text-3xl font-semibold text-white mb-1 truncate uppercase"
                                data-testid="card-title"
                            >
                                {channel.name}
                            </h3>
                            {isGift && (
                                <span className="absolute top-0 right-0 text-2xl">
                                    🎁
                                </span>
                            )}
                        </div>

                        <p className="text-sm text-gray-300 mb-2 line-clamp-2 break-words overflow-hidden">
                            {detailedChannel.creator?.display_name ||
                            detailedChannel.uploader?.display_name
                                ? `Curated ArtCast | ${
                                      channel.artworks?.length || 0
                                  } artworks`
                                : `Curated ArtCast | ${
                                      channel.artworks?.length || 0
                                  } artworks`}
                        </p>

                        {/* Removed the old curator text
                            {detailedChannel.creator?.display_name ||
                            detailedChannel.uploader?.display_name
                                ? `Curated Artcast by ${
                                      detailedChannel.creator?.display_name ||
                                      detailedChannel.uploader?.display_name
                                  }`
                                : channel.description}
                        </p>
                        */}

                        {/* Genre Badges - Updated to gray background without emojis */}
                        {(channel.genre || channel.secondary_genre) && (
                            <div className="mb-2 flex items-center gap-2 flex-wrap">
                                {channel.genre && (
                                    <div className="inline-flex items-center bg-gray-600/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
                                        <span className="text-sm font-medium text-white tracking-wide">
                                            {getGenreName(channel.genre)}
                                        </span>
                                    </div>
                                )}
                                {channel.secondary_genre &&
                                    channel.secondary_genre !==
                                        channel.genre && (
                                        <div className="inline-flex items-center bg-gray-600/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
                                            <span className="text-sm font-medium text-white tracking-wide">
                                                {getGenreName(
                                                    channel.secondary_genre
                                                )}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Tags removed as per requirements */}

                        {/* Removed duplicate dots section - already moved to top */}

                        {/* PLAY ON SCREEN Button moved after View All */}

                        {/* Current Artwork Preview */}
                        {currentArtwork && (
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentArtworkIndex}
                                    className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-2 flex items-center gap-3 w-full overflow-hidden mt-4"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.2 }}
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        e.nativeEvent.stopImmediatePropagation();
                                        console.log(
                                            '🎯 Current artwork click - should NOT affect selection'
                                        );
                                        // Clicked current artwork preview - start preview if not already previewing
                                        setPreviewPhase('beginning'); // Reset to beginning phase
                                        if (!isPreviewing) {
                                            console.log(
                                                '🎆 Starting preview from current artwork click for channel:',
                                                channel.name
                                            );
                                            setIsPreviewing(true);
                                            onPreviewChange?.(true);
                                        }
                                    }}
                                    onTouchStart={(e: React.TouchEvent) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        e.nativeEvent.stopImmediatePropagation();
                                        console.log(
                                            '🎯 Current artwork touch start blocked'
                                        );
                                    }}
                                    onTouchEnd={(e: React.TouchEvent) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        e.nativeEvent.stopImmediatePropagation();
                                        console.log(
                                            '🎯 Current artwork touch end - should NOT affect selection'
                                        );
                                        // Touched current artwork preview - start preview if not already previewing
                                        setPreviewPhase('beginning'); // Reset to beginning phase
                                        if (!isPreviewing) {
                                            console.log(
                                                '🎆 Starting preview from current artwork touch for channel:',
                                                channel.name
                                            );
                                            setIsPreviewing(true);
                                            onPreviewChange?.(true);
                                        }
                                    }}
                                >
                                    {/* Thumbnail */}
                                    <div className="relative w-16 h-9 sm:w-20 sm:h-11 rounded-lg overflow-hidden flex-shrink-0">
                                        <img
                                            src={getArtworkThumbnail(
                                                currentArtwork
                                            )}
                                            alt={currentArtwork.title}
                                            className="w-full h-full object-cover"
                                        />
                                        {isVideo(currentArtwork) && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                                    <svg
                                                        className="w-4 h-4 text-white ml-0.5"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                        <h4 className="text-sm font-bold text-white truncate">
                                            {detailedArtwork?.artist
                                                ?.display_name ||
                                                currentArtwork.artist}
                                        </h4>
                                        <p className="text-xs text-gray-400 truncate">
                                            {currentArtwork.title}
                                            {detailedArtwork?.creation_date
                                                ? `, ${detailedArtwork.creation_date}`
                                                : ''}
                                        </p>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        )}

                        {/* Expand/Collapse Button - Only clickable when previewing */}
                        {channel.artworks && channel.artworks.length > 1 && (
                            <div
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    e.nativeEvent.stopImmediatePropagation();
                                    console.log(
                                        '🔘 View All clicked - should not select card'
                                    );
                                    // Toggling expanded state - keep preview running
                                    const newExpanded = !isExpanded;
                                    onExpansionChange?.(
                                        newExpanded,
                                        channel.id
                                    );
                                    // Note: Preview continues running when expanding to show all artworks
                                }}
                                onMouseDown={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    e.nativeEvent.stopImmediatePropagation();
                                }}
                                onMouseUp={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    e.nativeEvent.stopImmediatePropagation();
                                }}
                                onTouchStart={(e: React.TouchEvent) => {
                                    e.stopPropagation();
                                    e.nativeEvent.stopImmediatePropagation();
                                }}
                                onTouchEnd={(e: React.TouchEvent) => {
                                    e.stopPropagation();
                                    e.nativeEvent.stopImmediatePropagation();
                                }}
                                className="w-full mt-2 text-center text-sm text-gray-400 font-medium py-2 hover:text-gray-300 transition-colors truncate px-2 cursor-pointer select-none"
                                data-testid="view-all-button"
                                role="button"
                                tabIndex={0}
                                style={{
                                    pointerEvents: 'auto',
                                    position: 'relative',
                                    zIndex: 50,
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const newExpanded = !isExpanded;
                                        onExpansionChange?.(
                                            newExpanded,
                                            channel.id
                                        );
                                    }
                                }}
                            >
                                {isExpanded
                                    ? 'Show Less'
                                    : `View All ${channel.artworks.length}`}
                            </div>
                        )}

                        {/* Expanded Artwork List */}
                        <AnimatePresence>
                            {isExpanded && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="mt-3 overflow-hidden"
                                    data-testid="expanded-artwork-list"
                                >
                                    <div className="space-y-2 max-h-64 overflow-y-auto overflow-x-hidden custom-scrollbar pr-2 bg-gray-900/95 backdrop-blur-sm rounded-lg p-2">
                                        {channel.artworks?.map(
                                            (artwork, index) => {
                                                const detailedArt =
                                                    artwork as unknown as DetailedArtwork;
                                                return (
                                                    <motion.div
                                                        key={artwork.id}
                                                        initial={{
                                                            opacity: 0,
                                                            x: -20,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            x: 0,
                                                        }}
                                                        transition={{
                                                            delay: index * 0.05,
                                                        }}
                                                        className={classNames(
                                                            'flex items-center gap-3 p-2 rounded-lg transition-colors',
                                                            index ===
                                                                currentArtworkIndex
                                                                ? 'bg-purple-500/20'
                                                                : 'active:bg-gray-700/50'
                                                        )}
                                                        data-testid="artwork-item"
                                                        onClick={(
                                                            e: React.MouseEvent
                                                        ) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            e.nativeEvent.stopImmediatePropagation();
                                                            console.log(
                                                                '🎯 Artwork click - should NOT affect selection'
                                                            );
                                                            // Clicked artwork - set index and start preview
                                                            setCurrentArtworkIndex(
                                                                index
                                                            );
                                                            setPreviewPhase(
                                                                'beginning'
                                                            ); // Reset to beginning phase
                                                            if (!isPreviewing) {
                                                                console.log(
                                                                    '🎆 Starting preview from artwork selection for channel:',
                                                                    channel.name
                                                                );
                                                                setIsPreviewing(
                                                                    true
                                                                );
                                                                onPreviewChange?.(
                                                                    true
                                                                );
                                                            }
                                                        }}
                                                        onTouchStart={(
                                                            e: React.TouchEvent
                                                        ) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            e.nativeEvent.stopImmediatePropagation();
                                                            console.log(
                                                                '🎯 Artwork touch start blocked'
                                                            );
                                                        }}
                                                        onTouchEnd={(
                                                            e: React.TouchEvent
                                                        ) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            e.nativeEvent.stopImmediatePropagation();
                                                            console.log(
                                                                '🎯 Artwork touch end - should NOT affect selection'
                                                            );
                                                            // Touched artwork - set index and start preview
                                                            setCurrentArtworkIndex(
                                                                index
                                                            );
                                                            setPreviewPhase(
                                                                'beginning'
                                                            ); // Reset to beginning phase
                                                            if (!isPreviewing) {
                                                                console.log(
                                                                    '🎆 Starting preview from artwork touch for channel:',
                                                                    channel.name
                                                                );
                                                                setIsPreviewing(
                                                                    true
                                                                );
                                                                onPreviewChange?.(
                                                                    true
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={getArtworkThumbnail(
                                                                artwork
                                                            )}
                                                            alt={artwork.title}
                                                            className="w-16 h-9 rounded object-cover"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <h5 className="text-xs font-bold text-white truncate">
                                                                {detailedArt
                                                                    ?.artist
                                                                    ?.display_name ||
                                                                    artwork.artist}
                                                            </h5>
                                                            <p className="text-xs text-gray-400 truncate">
                                                                {artwork.title}
                                                                {detailedArt?.creation_date
                                                                    ? `, ${detailedArt.creation_date}`
                                                                    : ''}
                                                            </p>
                                                        </div>
                                                    </motion.div>
                                                );
                                            }
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* PLAY ON SCREEN Button - Always visible for better UX */}
                        <AnimatePresence mode="wait">
                            {
                                <motion.button
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        // Call onPlayOnScreen to handle the complete flow
                                        if (onPlayOnScreen) {
                                            onPlayOnScreen();
                                        }
                                    }}
                                    className={`w-full mt-4 mb-2 font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 relative overflow-hidden transition-colors duration-75 ${
                                        isPreviewing
                                            ? isGift
                                                ? 'bg-gradient-to-b from-[#34D399] via-[#10B981] to-[#059669] shadow-[0_6px_30px_rgba(16,185,129,0.5)] text-white'
                                                : 'bg-gradient-to-b from-[#E879F9] via-[#D946EF] to-[#A855F7] shadow-[0_6px_30px_rgba(217,70,239,0.5)] text-white'
                                            : 'bg-white text-black'
                                    }`}
                                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: [0.4, 0.0, 0.2, 1],
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* 3D highlight effect - only when previewing */}
                                    {isPreviewing && (
                                        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
                                    )}

                                    {/* Checkmark Icon - Style based on preview state */}
                                    <div
                                        className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                            isPreviewing
                                                ? 'bg-white'
                                                : 'bg-black'
                                        }`}
                                    >
                                        <svg
                                            className={`w-4 h-4 ${
                                                isPreviewing
                                                    ? isGift
                                                        ? 'text-green-600'
                                                        : 'text-purple-600'
                                                    : 'text-white'
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                    <span className="text-lg tracking-wide">
                                        {isGift
                                            ? hasDeviceParams
                                                ? 'PLAY GIFT ON SCREEN'
                                                : 'COLLECT GIFT'
                                            : hasDeviceParams
                                            ? 'PLAY ON SCREEN'
                                            : 'SELECT ARTCAST'}
                                    </span>
                                </motion.button>
                            }
                        </AnimatePresence>
                    </div>
                </motion.div>
            </motion.div>
        );
    } catch (error) {
        console.error('[MobileArtcastCard] Runtime error:', error);
        return (
            <div className="relative rounded-2xl bg-gradient-to-br from-gray-800 to-gray-850 border-2 border-gray-700 p-4">
                <div className="text-white text-center">
                    Error rendering artcast
                </div>
            </div>
        );
    }
}
